import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus } from 'lucide-react';
import DeleteModal from 'Common/DeleteModal';
import { getRoleList } from 'services/role.service';
import { getUserRole } from 'services/user.service';
import {
  getUserList,
  addUser,
  updateUser,
  deleteUser,
  getUserDetail,
} from '../../slices/users/thunk';
import UserTable from './UserTable';
import UserFilter from './UserFilter';
import UserModal from './UserModal';
import { createSelector } from '@reduxjs/toolkit';
import { sortByLastModification } from 'helpers/utils/sortByLastModification';
import Loading from 'Common/Components/Loading';

const UserListView = () => {
  document.title = `User | EngageHub IQ`;
  const [user, setUser] = useState<any[]>([]);
  const [eventData, setEventData] = useState<any>();
  const [show, setShow] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [roles, setRoles] = useState<any[]>([]);
  const [roleName, setRoleName] = useState('user');
  const [rolesForUser, setRolesForUser] = useState<{ [key: string]: string }>({});
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [isReset, setIsReset] = useState<boolean>(false);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [searchText, setSearchText] = useState("");

  const dispatch = useDispatch<any>();
  const selectDataList = createSelector(
    (state: any) => state.Users,
    (user) => ({
      userList: user?.userList,
      userDetail: user?.userDetail,
      error: user.error,
      loading: user.loading,
    })
  );

  const { userList, userDetail, error, loading } = useSelector(selectDataList);

  useEffect(() => {
    dispatch(getUserList());
  }, [dispatch]);

  useEffect(() => {
    if (userList && userList.length > 0) {
      const sortedUsers = sortByLastModification(userList);
      setUser(sortedUsers);
    }
  }, [userList]);

  useEffect(() => {
    if (Array.isArray(user)) {
      user.forEach((user: any) => {
        getUserRole(user.id)
          .then((res: any) => {
            if (res?.items?.length > 0) {
              setRolesForUser((prev) => ({ ...prev, [user.id]: res.items[0]?.name.toUpperCase() }));
            }
          })
          .catch((err: any) => err);
      });
    }
  }, [user]);

  useEffect(() => {
    getRoleList()
      .then((res: any) => {
        if (res) setRoles(res.items);
      })
      .catch((err: any) => err);
  }, []);

  useEffect(() => {
    const role = localStorage.getItem('role') || '';
    setRoleName(role);
  }, []);

  useEffect(() => {
    if (error == null && !loading && isSubmitted) {
      toggle();
      setIsSubmitted(false);
    }
  }, [error, isSubmitted, loading]);

  const toggle = useCallback(() => {
    setShow((prev) => !prev);
    if (show) {
      setIsReset(false);
      setIsEdit(false);
    }
  }, [show]);

  const handleDelete = () => {
    if (userDetail?.id) {
      dispatch(deleteUser(userDetail.id));
      setDeleteModal(false);
    }
  };

  const onClickDelete = (cell: any) => {
    setIsDelete(true);
    setDeleteModal(true);
    if (cell?.id) {
      dispatch(getUserDetail(cell.id));
      setEventData(cell);
    }
  };

  const handleUpdateDataClick = (ele: any) => {
    if (ele?.id) {
      dispatch(getUserDetail(ele.id));
    }
    setEventData({ ...ele, role: rolesForUser[ele?.id] });
    setIsEdit(true);
    setShow(true);
  };

  const handleEditSubmit = async (values: any) => {
    const payload = {
      userName: values.userName,
      name: values.firstName,
      surname: values.lastName,
      email: values.email,
      phoneNumber: values.phoneNumber,
      roleNames: values.role === '' ? null : [values.role],
      ...(values.password !== '' && { password: values.password }),
      isActive: values.isActive,
    };
    if (payload && userDetail?.id) {
      await dispatch(updateUser({ id: userDetail.id, payload })).then((res: any) => {
        if (!res?.error) {
          setIsSubmitted(true);
        }
      });
    }
  };

  const handleAddSubmit = async (values: any) => {
    const payload = {
      userName: values.userName,
      name: values.firstName,
      surname: values.lastName,
      email: values.email,
      phoneNumber: values.phoneNumber,
      password: values.password,
      roleNames: values.role === '' ? null : [values.role],
      isActive: values.isActive,
    };
    if (payload) {
      await dispatch(addUser(payload)).then((res: any) => {
        if (!!res?.error) {
          return
        } else {
          setIsReset(true);
          setIsSubmitted(true);
        }
      })
    }
  };

  const keysToSearch = ['userName', 'email'];
  const filteredEventData = Array.isArray(user)
    ? user.filter((item: any) =>
      keysToSearch.some((key: any) =>
        item[key]?.toLowerCase().includes(searchText.toLowerCase())
      )
    )
    : [];

  const handleOnSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  }

  const handleStatusChange = (selectedOption: any) => {
    if (selectedOption.value === 'Hidden') {
      setUser(userList);
    } else {
      const filteredUsers = userList?.filter((data: any) => data?.isActive === selectedOption.value);
      setUser(filteredUsers);
    }
  };

  return (
    <React.Fragment>
      <DeleteModal
        title="Are you sure you want to proceed?"
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        onDelete={handleDelete} />
      <div className="w-full h-[calc(100vh-70px)] bg-white overflow-hidden">
        <div className="p-2 sm:p-3 md:p-4 lg:p-5 !mb-0" id="usersTable">
          <div className="overflow-x-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
              <div className="w-full sm:w-auto">
                <h6 className="text-15 mb-3">User Management</h6>
              </div>
            </div>
          </div>
          <UserFilter onClearSearch={() => { setSearchText(""); }} textValue={searchText} onSearch={handleOnSearch} onStatusChange={handleStatusChange} />
          {loading && !isDelete
            ? <div className="min-h-[100px] mt-3"><Loading size="sm" color="blue" isVisible={true} /></div>
            :
            <>              {(roleName === 'admin' && !loading) && (
              <div className='mt-2 flex items-center justify-center sm:justify-end me-0 sm:me-2'>
                <button
                  onClick={toggle}
                  type="button"
                  className="text-xs sm:text-sm text-white btn bg-blue-500 hover:bg-blue-600 hover:text-white px-3 py-2 w-full sm:w-auto"
                >
                  <Plus className="inline-block size-4" /> <span className="align-middle">Add User</span>
                </button>
              </div>
            )}
              <UserTable
                users={filteredEventData}
                roleName={roleName}
                rolesForUser={rolesForUser}
                onEdit={handleUpdateDataClick}
                onDelete={onClickDelete}
              />
            </>
          }
        </div>
      </div>
      <UserModal
        show={show}
        isEdit={isEdit}
        eventData={eventData}
        roles={roles}
        onHide={toggle}
        onSubmit={isEdit ? handleEditSubmit : handleAddSubmit}
        isReset={isReset}
      />
    </React.Fragment>
  );
};

export default UserListView;